import type React from "react";

type ImageToolbarState = {
  visible: boolean;
  top: number;
  left: number;
  style: string;
  alt: string;
  width: string;
  height: string;
  src: string;
  isExternal: boolean;
  index: number | null;
};

type ImageToolbarProps = {
  state: ImageToolbarState;
  isHtmlMode: boolean;
  isImageModalOpen: boolean;
  onApplyStyle: (nextStyle: string) => void;
  onApplyAlt: (alt: string) => void;
  onDeleteImage: () => void;
  onDraftAltChange: (alt: string) => void;
  stopEditorShortcutPropagation: (event: { key: string; ctrlKey?: boolean; metaKey?: boolean; stopPropagation: () => void }) => void;
  toolbarRef: React.RefObject<HTMLDivElement | null>;
};

export function ImageToolbar({
  state,
  isHtmlMode,
  isImageModalOpen,
  onApplyStyle,
  onApplyAlt,
  onDeleteImage,
  onDraftAltChange,
  stopEditorShortcutPropagation,
  toolbarRef,
}: ImageToolbarProps) {
  if (!state.visible || isHtmlMode || isImageModalOpen) return null;

  return (
    <div
      ref={toolbarRef}
      className="eb-QuillText__image-toolbar"
      style={{
        top: `${state.top}px`,
        left: `${state.left}px`,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="eb-QuillText__image-toolbar-row">
        <button
          type="button"
          className={["eb-QuillText__image-toolbar-btn", state.style === "centered" ? "is-active" : ""].filter(Boolean).join(" ")}
          onClick={() => onApplyStyle("centered")}
        >
          Center
        </button>
        <button
          type="button"
          className={["eb-QuillText__image-toolbar-btn", state.style === "float-left" ? "is-active" : ""].filter(Boolean).join(" ")}
          onClick={() => onApplyStyle("float-left")}
        >
          Left
        </button>
        <button
          type="button"
          className={["eb-QuillText__image-toolbar-btn", state.style === "float-right" ? "is-active" : ""].filter(Boolean).join(" ")}
          onClick={() => onApplyStyle("float-right")}
        >
          Right
        </button>
      </div>
      <div className="eb-QuillText__image-toolbar-row eb-QuillText__image-toolbar-row--fields">
        <label className="eb-QuillText__image-meta-field eb-QuillText__image-meta-field--alt">
          <span className="eb-QuillText__image-meta-label">Alt</span>
          <input
            type="text"
            value={state.alt}
            placeholder="Image description"
            onChange={(e) => onDraftAltChange(e.target.value)}
            onBlur={(e) => onApplyAlt(e.target.value)}
            onKeyDown={(e) => {
              stopEditorShortcutPropagation(e);
              if (e.key === "Enter") {
                e.preventDefault();
                onApplyAlt((e.target as HTMLInputElement).value);
              }
            }}
          />
        </label>
      </div>
      <div className="eb-QuillText__image-toolbar-row eb-QuillText__image-toolbar-row--fields">
        <label className="eb-QuillText__image-meta-field eb-QuillText__image-meta-field--alt">
          <span className="eb-QuillText__image-meta-label">URL</span>
          <input type="text" value={state.src} readOnly />
        </label>
      </div>
      <div className="eb-QuillText__image-toolbar-row">
        <p className="eb-QuillText__image-alt-helper">
          Source: {state.isExternal ? "External" : "Local"}
        </p>
      </div>
      <div className="eb-QuillText__image-toolbar-row">
        <p className="eb-QuillText__image-alt-helper">
          Alt text helps screen readers describe images to visually impaired users and improves SEO.
        </p>
      </div>
      <div className="eb-QuillText__image-toolbar-row">
        <button
          type="button"
          className="eb-QuillText__image-toolbar-btn eb-QuillText__image-toolbar-btn--delete"
          onClick={onDeleteImage}
        >
          Delete Image
        </button>
      </div>
    </div>
  );
}

